import { format } from 'date-fns';
import { AccommodationServiceProviderTypeEnum } from 'features/common/components/AutocompleteAccomodationProviders';
import { getAccomodationProviderId, putServiceForm } from 'features/salesOrder/services';
import { ServiceUnitEnum, TransferTypeEnum, TypeServiceEnum } from 'features/salesOrder/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import { formatFlightLimitDateForApi } from './flightServicePayload';

const formatAccommodationDateForApi = (value: unknown): string => {
	if (typeof value !== 'string' || !value.trim()) {
		return '';
	}
	const trimmed = value.trim();
	if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
		return trimmed;
	}
	try {
		return format(new Date(trimmed), 'yyyy-MM-dd');
	} catch {
		return trimmed;
	}
};

const normalizeServiceUnit = (unit: unknown): string | undefined => {
	if (Array.isArray(unit)) {
		const first = unit[0];
		return typeof first === 'string' && first.trim() ? first : ServiceUnitEnum.Person;
	}
	if (typeof unit === 'string' && unit.trim()) {
		return unit;
	}
	return undefined;
};

export const saveServiceToOrder = async (saleOrderId: string, service: Record<string, unknown>): Promise<void> => {
	const payload = { ...service };

	const normalizedUnit = normalizeServiceUnit(payload.unit);
	if (normalizedUnit) {
		payload.unit = normalizedUnit;
	}

	if (payload.gds === 'n/a') {
		delete payload.gds;
	}
	if (payload.oidIssueId === 'n/a') {
		delete payload.oidIssueId;
	}
	if (payload.tariffProvider === 'n/a') {
		delete payload.tariffProvider;
	}
	if (payload.tariffProviderId === 'n/a') {
		delete payload.tariffProviderId;
	}
	if (payload.serviceProviderId === 'n/a') {
		delete payload.serviceProviderId;
	}

	if (payload.serviceType === 'Flight') {
		delete payload.cost;
		delete payload.saleAmount;

		if (Array.isArray(payload.vc) && payload.vc.length > 0) {
			payload.vc = payload.vc[0];
		}
		if (typeof payload.limitDate === 'string') {
			payload.limitDate = formatFlightLimitDateForApi(payload.limitDate);
		}

		const groups = payload.passengersGroupByPassengerType;
		if (Array.isArray(groups)) {
			payload.passengersGroupByPassengerType = groups.map((group: Record<string, unknown>) => {
				const passengerIds = (group.passengerIds as string[]) || [];
				const existingTickets = (group.passengersTickets as { passengerId: string; ticket: string }[]) || [];
				const ticketsByPax = new Map(existingTickets.map((t) => [t.passengerId, t.ticket]));

				return {
					...group,
					cost: Number(group.cost) || 0,
					saleAmount: Number(Number(group.saleAmount || 0).toFixed(2)),
					totalCost: Number(group.totalCost) || 0,
					totalSaleAmount: Number(Number(group.totalSaleAmount || 0).toFixed(2)),
					markupPercentage: Number(group.markupPercentage ?? payload.markupPercentage) || 0,
					passengersTickets: passengerIds.map((passengerId) => ({
						passengerId,
						ticket: ticketsByPax.get(passengerId) || '-',
					})),
				};
			});
		}

		if (payload.flightLegs?.length) {
			payload.withTst = true;
			for (let i = 0; i < payload.flightLegs.length; i += 1) {
				if (!payload.flightLegs[i].keyId) {
					payload.flightLegs[i].keyId = `leg-${i}`;
				}
			}
		} else {
			payload.withTst = false;
			payload.flightLegs = [];
		}
		if (payload.markupPercentage !== undefined) {
			payload.markupPercentage = Number(payload.markupPercentage);
		}
	}

	if (payload.serviceType === TypeServiceEnum.Accommodation) {
		if (payload.unit === ServiceUnitEnum.PerRoom) {
			delete payload.cost;
			delete payload.saleAmount;
		}

		const serviceProviderId = payload.serviceProviderId;
		const providerType = payload.accommodationServiceProviderType as AccommodationServiceProviderTypeEnum | undefined;
		if (typeof serviceProviderId === 'string' && serviceProviderId && serviceProviderId !== 'n/a' && providerType) {
			try {
				const result = await getAccomodationProviderId(serviceProviderId, providerType);
				payload.serviceProvider = result.data;
				delete payload.serviceProviderId;
			} catch (error) {
				throw new Error(
					extractErrorMessage(error, 'No se pudo resolver el prestador del hotel. Verificá el hotel en la cotización.'),
				);
			}
		}
		delete payload.accommodationServiceProviderType;

		['startDate', 'endDate', 'limitDate'].forEach((field) => {
			if (payload[field] !== undefined) {
				payload[field] = formatAccommodationDateForApi(payload[field]);
			}
		});

		if (Array.isArray(payload.rooms)) {
			payload.rooms = payload.rooms.map((room: Record<string, unknown>) => ({
				...room,
				cost: room.cost !== undefined ? String(room.cost) : room.cost,
				saleAmount: room.saleAmount !== undefined ? String(Number(room.saleAmount).toFixed(2)) : room.saleAmount,
				markupPercentage: room.markupPercentage !== undefined ? String(room.markupPercentage) : room.markupPercentage,
			}));
		}

		if (payload.totalCost !== undefined) {
			payload.totalCost = String(payload.totalCost);
		}
		if (payload.totalSaleAmount !== undefined) {
			payload.totalSaleAmount = String(Number(payload.totalSaleAmount).toFixed(2));
		}
	}

	if (payload.serviceType === TypeServiceEnum.Other) {
		['startDate', 'endDate', 'limitDate'].forEach((field) => {
			if (payload[field] !== undefined) {
				payload[field] = formatAccommodationDateForApi(payload[field]);
			}
		});
		if (payload.cost !== undefined) {
			payload.cost = String(payload.cost);
		}
		if (payload.saleAmount !== undefined) {
			payload.saleAmount = String(Number(payload.saleAmount).toFixed(2));
		}
		if (payload.totalCost !== undefined) {
			payload.totalCost = String(payload.totalCost);
		}
		if (payload.totalSaleAmount !== undefined) {
			payload.totalSaleAmount = String(Number(payload.totalSaleAmount).toFixed(2));
		}
		if (typeof payload.reference === 'string' && !payload.reference.trim()) {
			payload.reference = '-';
		}
		if (payload.sameTariffServiceProvider) {
			payload.serviceProviderId = payload.tariffProviderId;
		}
	}

	if (payload.serviceType === TypeServiceEnum.Transfer) {
		if (payload.sameTariffServiceProvider) {
			payload.serviceProviderId = payload.tariffProviderId;
		}
		if (typeof payload.reference === 'string' && !payload.reference.trim()) {
			payload.reference = '-';
		}
		if (Array.isArray(payload.transferSegments)) {
			payload.transferSegments = payload.transferSegments.map((segment: Record<string, unknown>) => {
				const next = { ...segment };
				if (next.startDate !== undefined) {
					next.startDate = formatAccommodationDateForApi(next.startDate);
				}
				const type = next.transferType;
				const isRoundTrip = type === TransferTypeEnum.InOut || type === TransferTypeEnum.InOutPrivate;
				if (isRoundTrip && next.endDate !== undefined) {
					next.endDate = formatAccommodationDateForApi(next.endDate);
				} else {
					delete next.endDate;
				}
				return next;
			});
		}
		if (payload.cost !== undefined) {
			payload.cost = String(payload.cost);
		}
		if (payload.saleAmount !== undefined) {
			payload.saleAmount = String(Number(payload.saleAmount).toFixed(2));
		}
		if (payload.totalCost !== undefined) {
			payload.totalCost = String(payload.totalCost);
		}
		if (payload.totalSaleAmount !== undefined) {
			payload.totalSaleAmount = String(Number(payload.totalSaleAmount).toFixed(2));
		}
	}

	if (payload.serviceType === TypeServiceEnum.FlightAncillary) {
		if (Array.isArray(payload.vc) && payload.vc.length > 0) {
			payload.vc = payload.vc[0];
		}
		if (typeof payload.reference === 'string' && !payload.reference.trim()) {
			payload.reference = '-';
		}
		if (typeof payload.limitDate === 'string') {
			payload.limitDate = formatFlightLimitDateForApi(payload.limitDate);
		}
		if (payload.cost !== undefined) {
			payload.cost = String(payload.cost);
		}
		if (payload.saleAmount !== undefined) {
			payload.saleAmount = String(Number(payload.saleAmount).toFixed(2));
		}
		if (payload.totalCost !== undefined) {
			payload.totalCost = String(payload.totalCost);
		}
		if (payload.totalSaleAmount !== undefined) {
			payload.totalSaleAmount = String(Number(payload.totalSaleAmount).toFixed(2));
		}
	}

	if (payload.serviceType === TypeServiceEnum.Fee) {
		if (payload.cost !== undefined) {
			payload.cost = String(payload.cost);
		}
		if (payload.saleAmount !== undefined) {
			payload.saleAmount = String(Number(payload.saleAmount).toFixed(2));
		}
		if (payload.totalCost !== undefined) {
			payload.totalCost = String(payload.totalCost);
		}
		if (payload.totalSaleAmount !== undefined) {
			payload.totalSaleAmount = String(Number(payload.totalSaleAmount).toFixed(2));
		}
	}

	try {
		await putServiceForm({
			saleOrderId,
			serviceId: String(payload.keyId),
			data: payload,
		});
	} catch (error) {
		throw new Error(extractErrorMessage(error, 'Error al guardar el servicio'));
	}
};
